import { Image } from 'expo-image';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';

import { ConversationBubbleContextMenu } from './ConversationBubbleContextMenu';
import { ConversationTimestamp } from './ConversationTimestamp';

import { ProfilePicture } from '@/components/domain/common/ProfilePicture';
import { VStack } from '@/components/gluestack/vstack';
import { RenderHtml } from '@/components/ui/RenderHtml';
import { MediaView } from '@/components/ui/media/MediaView';
import { ImageViewerTrigger } from '@/components/ui/image-viewer/ImageViewerTrigger';
import { ProtectedImageContainer } from '@/components/ui/ProtectedImageContainer';
import { ConversationItem } from '@/models/Conversation';
import type { Member } from '@/models/Member';
import type { Photo } from '@/models/Photo';

interface ConversationBubbleProps {
  item: ConversationItem;
  isTimestampVisible: boolean;
  selfMember?: Member;
  otherMember?: Member;
  /** Lazily resolves the conversation's shared photo carousel at viewer open time. */
  getPhotos?: () => Photo[] | undefined;
}

export const ConversationBubble = React.memo(
  ({ item, isTimestampVisible, selfMember, otherMember, getPhotos }: ConversationBubbleProps) => {
    const { t } = useTranslation();
    const isFromMe = item.IsFromMe;
    const hasPhoto = item.PhotoThumbnailUrl;
    const hasMedia = !!item.Media;
    const hasText = item.Text && !hasMedia;
    const textClassName = [
      'text-base',
      Platform.OS === 'web' ? '[overflow-wrap:anywhere]' : '',
      isFromMe ? 'text-white' : '',
    ]
      .filter(Boolean)
      .join(' ');

    // Define the bubble's rounded corner classes
    const bubbleRounding = isFromMe ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-bl-md';

    return (
      <VStack>
        <ConversationTimestamp currentItem={item} isVisible={isTimestampVisible} />
        <View className="mx-4 my-1">
          <View
            className={`flex-row items-end ${isFromMe ? 'justify-end' : 'justify-start'} gap-2`}>
            {!isFromMe && otherMember ? (
              <ProfilePicture member={otherMember} size="xs" showPresence={false} showTooltip />
            ) : null}

            <VStack className="max-w-[75%] gap-1">
              {/* Photo attachment */}
              {hasPhoto ? (
                <View
                  className={`flex-row items-center gap-2 ${
                    isFromMe ? 'justify-end' : 'justify-start'
                  }`}>
                  <ImageViewerTrigger
                    uri={item.PhotoFullSizeUrl!}
                    getPhotos={getPhotos}
                    disableSocialActions>
                    <ProtectedImageContainer>
                      <Image
                        source={{ uri: item.PhotoThumbnailUrl! }}
                        className={`h-48 w-48 ${bubbleRounding}`}
                        contentFit="cover"
                        recyclingKey={item.PhotoThumbnailUrl}
                        enforceEarlyResizing
                      />
                    </ProtectedImageContainer>
                  </ImageViewerTrigger>
                </View>
              ) : null}

              {hasMedia ? (
                <ConversationBubbleContextMenu item={item}>
                  <View
                    className={`${bubbleRounding} overflow-hidden ${
                      isFromMe ? 'bg-primary-500' : 'bg-background-100'
                    }`}>
                    <MediaView item={item.Media!} rounded={false} />
                  </View>
                </ConversationBubbleContextMenu>
              ) : null}

              {/* Text content */}
              {hasText ? (
                <ConversationBubbleContextMenu item={item}>
                  <View
                    className={`${bubbleRounding} px-4 py-1 ${
                      isFromMe ? 'bg-primary-500' : 'bg-background-100'
                    }`}>
                    <RenderHtml
                      html={item.Text}
                      noTruncate
                      detectHttpsLinks
                      className={textClassName}
                      linkClassName={
                        isFromMe ? 'text-white underline' : 'text-primary-500 underline'
                      }
                      size="md"
                      androidBoldTextTrailingSpaces={2}
                    />
                  </View>
                </ConversationBubbleContextMenu>
              ) : null}
            </VStack>

            {isFromMe && selfMember ? (
              <ProfilePicture member={selfMember} size="xs" showPresence={false} />
            ) : null}
          </View>
        </View>
      </VStack>
    );
  }
);
